import {faEllipsisVertical, faPenToSquare, faTrash} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Dropdown, Flex, Space} from 'antd';
import {ReactElement, useState} from 'react';

import {Text} from '../../../../../../../component/text/text';
import {useDomainConfig} from '../../../../../../../provider/domain-config/domain-config-hook';
import {useLocale} from '../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../provider/locale/localization';
import {NoteFormType} from '../../../../../../../service/reviews/reviews-notes';
import {useFormat} from '../../../../../../../util/format-hook/format-hook';
import {NoteModal} from '../../../review-list/review/review-header/note-modal/note-modal';
import ScrappyImgSrc from '../common/scrappy.png';
import * as styles from '../new-note.scss';

type PropsType = {
    createdAt?: string;
    updatedAt?: string | null;
    userEmail?: string;
    handleDeleteClick: () => void;
    handleEdit?: (id: number | string, text: string) => void;
    noteText: string;
    noteId: string | number;
};

export function NoteUi(props: PropsType): ReactElement {
    const {createdAt, updatedAt, noteId, handleEdit, userEmail, noteText, handleDeleteClick} = props;
    const {getFormattedDateTime} = useFormat();
    const {companyName} = useDomainConfig();
    const {getLocalizedString} = useLocale();
    const [isOpenEditModal, setIsOpenEditModal] = useState<boolean>(false);

    const formattedDate = createdAt
        ? getFormattedDateTime(new Date(createdAt), {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
          })
        : null;

    const formattedUpdatedDate = updatedAt
        ? getFormattedDateTime(new Date(updatedAt), {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
          })
        : null;

    function onSubmitHandler(formValues: NoteFormType) {
        if (handleEdit) {
            handleEdit(noteId, formValues.text);
        }

        setIsOpenEditModal(false);
    }

    return (
        <Flex className={styles.Note} gap={6} vertical>
            <img alt="" className={styles.Note_scrappyIcon} src={ScrappyImgSrc} />
            <Flex justify="space-between">
                <Space size={4} wrap>
                    <Locale stringKey="REVIEWS__ENTER_NOTE_TITLE" />
                    <Text
                        bolder
                        stringKey="REVIEWS__RESPONSE_EMPLOYEE"
                        valueMap={{
                            name: <span className={styles.Note_email}>{userEmail || companyName}</span>,
                        }}
                    />
                    <Space align="start" size={4}>
                        <Text secondary>•</Text>
                        <Text secondary>
                            {formattedUpdatedDate ? (
                                <Space size={4} wrap>
                                    <Locale stringKey="REVIEWS__ENTER_NOTE_EDITED" />
                                    {formattedUpdatedDate}
                                </Space>
                            ) : (
                                formattedDate
                            )}
                        </Text>
                    </Space>
                </Space>
                <Dropdown
                    menu={{
                        items: [
                            {
                                icon: <FontAwesomeIcon icon={faPenToSquare} />,
                                label: getLocalizedString('REVIEWS__NOTE__EDIT'),
                                key: 'edit',
                                onClick: () => setIsOpenEditModal(true),
                            },
                            {
                                icon: <FontAwesomeIcon icon={faTrash} />,
                                label: getLocalizedString('REVIEWS__NOTE__DELETE'),
                                key: 'delete',
                                onClick: handleDeleteClick,
                            },
                        ],
                    }}
                    placement="bottomRight"
                    trigger={['click']}
                >
                    <Button type="text">
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                    </Button>
                </Dropdown>
            </Flex>
            <Text>{noteText}</Text>
            {isOpenEditModal && (
                <NoteModal
                    editableNoteText={noteText}
                    isLoading={false}
                    onCancel={setIsOpenEditModal}
                    onSubmit={onSubmitHandler}
                    title={<Locale stringKey="REVIEWS__NOTE__EDIT" />}
                />
            )}
        </Flex>
    );
}
